import React, { useState, useEffect, useRef } from "react";

import * as Yup from "yup";
import { Formik, Form } from "formik";
import { toast } from "react-toastify";

import AppModal from "../AppModal";
import AuthField from "../Auth/AuthField";
import { i18n } from "../../translate/i18n";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import { resolveUploadedFileUrl } from "../../helpers/resolveMediaUrl";

const QuickAnswerSchema = Yup.object().shape({
	shortcut: Yup.string()
		.min(2, "Too Short!")
		.max(15, "Too Long!")
		.required("Required"),
	message: Yup.string()
		.min(8, "Too Short!")
		.max(30000, "Too Long!")
		.required("Required"),
});

const initialState = {
	shortcut: "",
	message: "",
};

function IconAttach() {
	return (
		<svg className="size-4 shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
			<path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48" />
		</svg>
	);
}

function QuickAnswersModal({ open, onClose, quickAnswerId, initialValues, onSave, reload }) {
	const isMounted = useRef(true);
	const [quickAnswer, setQuickAnswer] = useState(initialState);
	const [file, setFile] = useState("");
	const [loading, setLoading] = useState(false);

	useEffect(
		() => () => {
			isMounted.current = false;
		},
		[]
	);

	const handleUpdateMedia = async (e) => {
		const img = e.target.files?.[0];
		if (!img) return;

		const formData = new FormData();
		formData.append("file", img);
		try {
			const response = await api.post("/bulk-messaging/upload-image", formData, {
				headers: { "Content-Type": "multipart/form-data" },
			});
			setFile(resolveUploadedFileUrl(img, response));
		} catch (err) {
			toastError(err);
		}
	};

	useEffect(() => {
		if (!open) return undefined;

		const fetchQuickAnswer = async () => {
			if (initialValues) {
				setQuickAnswer((prev) => ({ ...prev, ...initialValues }));
				setFile(initialValues.file || "");
			}

			if (!quickAnswerId) {
				setQuickAnswer(initialState);
				setFile("");
				return;
			}

			setLoading(true);
			try {
				const { data } = await api.get(`/quickAnswers/${quickAnswerId}`);
				if (isMounted.current) {
					setQuickAnswer(data);
					setFile(data.file || "");
				}
			} catch (err) {
				toastError(err);
			} finally {
				if (isMounted.current) setLoading(false);
			}
		};

		fetchQuickAnswer();
	}, [quickAnswerId, open, initialValues]);

	const handleClose = () => {
		onClose();
		setQuickAnswer(initialState);
		setFile("");
	};

	const handleSaveQuickAnswer = async (values) => {
		const payload = { ...values, file: file || null };

		try {
			if (quickAnswerId) {
				await api.put(`/quickAnswers/${quickAnswerId}`, payload);
			} else {
				const { data } = await api.post("/quickAnswers", payload);
				onSave?.(data);
			}
			toast.success(i18n.t("quickAnswersModal.success"));
			if (typeof reload === "function") {
				await reload();
			}
			handleClose();
		} catch (err) {
			toastError(err);
		}
	};

	const title = quickAnswerId
		? i18n.t("quickAnswersModal.title.edit")
		: i18n.t("quickAnswersModal.title.add");

	return (
		<Formik
			initialValues={quickAnswer}
			enableReinitialize
			validationSchema={QuickAnswerSchema}
			onSubmit={(values, actions) => {
				handleSaveQuickAnswer(values);
				actions.setSubmitting(false);
			}}
		>
			{({ values, errors, touched, isSubmitting, handleChange, handleBlur }) => (
				<AppModal
					open={open}
					onClose={handleClose}
					title={title}
					maxWidth={560}
					footer={
						<>
							<button
								type="button"
								className="btn btn-sub-gray"
								onClick={handleClose}
								disabled={isSubmitting}
							>
								{i18n.t("quickAnswersModal.buttons.cancel")}
							</button>
							<button
								type="submit"
								form="quick-answer-form"
								className="btn btn-primary"
								disabled={isSubmitting || loading}
							>
								{quickAnswerId
									? i18n.t("quickAnswersModal.buttons.okEdit")
									: i18n.t("quickAnswersModal.buttons.okAdd")}
							</button>
						</>
					}
				>
					{loading ? (
						<div className="app-modal-loading">
							<div className="app-modal-spinner" />
						</div>
					) : (
						<Form id="quick-answer-form" className="space-y-4">
							<AuthField
								id="quick-answer-shortcut"
								label={i18n.t("quickAnswersModal.form.shortcut")}
								error={touched.shortcut && errors.shortcut}
							>
								<input
									id="quick-answer-shortcut"
									name="shortcut"
									type="text"
									className="form-input w-full"
									value={values.shortcut}
									onChange={handleChange}
									onBlur={handleBlur}
									autoFocus
								/>
							</AuthField>

							<AuthField
								id="quick-answer-message"
								label={i18n.t("quickAnswersModal.form.message")}
								error={touched.message && errors.message}
							>
								<textarea
									id="quick-answer-message"
									name="message"
									className="form-input min-h-[8rem] w-full resize-y"
									value={values.message}
									onChange={handleChange}
									onBlur={handleBlur}
									rows={5}
								/>
							</AuthField>

							<div className="space-y-2">
								{file ? (
									<div className="relative rounded-md border border-default-200 bg-default-100 p-2 dark:border-default-600 dark:bg-default-800">
										<img
											src={file}
											alt=""
											className="mx-auto max-h-48 w-full max-w-full object-contain"
										/>
										<button
											type="button"
											className="btn btn-sm btn-sub-gray absolute end-2 top-2"
											onClick={() => setFile("")}
										>
											{i18n.t("quickAnswersModal.form.removeImage")}
										</button>
									</div>
								) : (
									<label className="btn btn-sub-gray inline-flex cursor-pointer">
										<IconAttach />
										{i18n.t("quickAnswersModal.form.attachImage")}
										<input
											type="file"
											accept="image/png,image/jpeg,image/jpg"
											className="sr-only"
											onChange={handleUpdateMedia}
										/>
									</label>
								)}
							</div>
						</Form>
					)}
				</AppModal>
			)}
		</Formik>
	);
}

export default QuickAnswersModal;
